// Minimal IPv4/IPv6 CIDR matching, no dependencies.

function parseV4(ip: string): bigint | null {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  let n = 0n;
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p)) return null;
    const v = Number(p);
    if (v > 255) return null;
    n = (n << 8n) | BigInt(v);
  }
  return n;
}

function parseV6(ip: string): bigint | null {
  if (ip.includes('.')) {
    // IPv4-mapped, e.g. ::ffff:1.2.3.4
    const i = ip.lastIndexOf(':');
    const v4 = parseV4(ip.slice(i + 1));
    if (v4 === null) return null;
    const head = parseV6(ip.slice(0, i + 1) + '0:0');
    if (head === null) return null;
    return (head & ~0xffffffffn) | v4;
  }
  const halves = ip.split('::');
  if (halves.length > 2) return null;
  const left = halves[0] ? halves[0].split(':') : [];
  const right = halves.length === 2 && halves[1] ? halves[1].split(':') : [];
  const missing = 8 - left.length - right.length;
  if (missing < 0 || (halves.length === 1 && missing !== 0)) return null;
  const groups = [...left, ...Array<string>(missing).fill('0'), ...right];
  let n = 0n;
  for (const g of groups) {
    if (!/^[0-9a-fA-F]{1,4}$/.test(g)) return null;
    n = (n << 16n) | BigInt(parseInt(g, 16));
  }
  return n;
}

export function parseIp(ip: string): { v: 4 | 6; n: bigint } | null {
  const s = ip.trim();
  if (s.includes(':')) {
    const n = parseV6(s);
    if (n === null) return null;
    // Normalise IPv4-mapped addresses to v4 so "::ffff:1.2.3.4" matches a v4 CIDR.
    if (n >> 32n === 0xffffn) return { v: 4, n: n & 0xffffffffn };
    return { v: 6, n };
  }
  const n = parseV4(s);
  return n === null ? null : { v: 4, n };
}

export function ipInCidr(ip: string, cidr: string): boolean {
  const addr = parseIp(ip);
  if (!addr) return false;
  const [base, bitsStr] = cidr.split('/');
  const net = parseIp(base ?? '');
  if (!net || net.v !== addr.v) return false;
  const width = addr.v === 4 ? 32 : 128;
  const bits = bitsStr === undefined ? width : Number(bitsStr);
  if (!Number.isInteger(bits) || bits < 0 || bits > width) return false;
  if (bits === 0) return true;
  const shift = BigInt(width - bits);
  return addr.n >> shift === net.n >> shift;
}

// Allowlist entries come from network docs in many shapes. Supported:
//   139.144.178.218            single IPv4 or IPv6
//   139.144.178.0/24           CIDR
//   139.144.178.10-139.144.178.50   dash range (spaces around "-" are fine)
//   139.144.178.10-50          short dash range (last octet only)
//   139.144.178.*  139.144.*.* wildcard octets (trailing only)

type Range = { v: 4 | 6; lo: bigint; hi: bigint };

/** Widest range accepted, so an entry like 0.0.0.0/0 can't quietly turn the allowlist off. */
const MAX_SPAN: Record<4 | 6, bigint> = { 4: 1n << 16n, 6: 1n << 96n };

function toRange(entry: string): Range | string {
  const e = entry.trim().replace(/\s*-\s*/, '-');
  if (!e) return 'empty entry';
  // Wildcards: 1.2.3.* → 1.2.3.0/24, 1.2.*.* → 1.2.0.0/16
  if (e.includes('*')) {
    const parts = e.split('.');
    const stars = parts.filter((x) => x === '*').length;
    const firstStar = parts.indexOf('*');
    if (parts.length !== 4 || firstStar < 0 || parts.slice(firstStar).some((x) => x !== '*'))
      return `"${entry}" is not a valid wildcard (use e.g. 1.2.3.*)`;
    return toRange(`${parts.map((x) => (x === '*' ? '0' : x)).join('.')}/${32 - 8 * stars}`);
  }
  if (e.includes('-')) {
    const [from, to, ...more] = e.split('-');
    const lo = parseIp(from ?? '');
    let hiText = to ?? '';
    // Short form 1.2.3.10-50: only the last octet given.
    if (lo?.v === 4 && /^\d{1,3}$/.test(hiText))
      hiText = `${from!.split('.').slice(0, 3).join('.')}.${hiText}`;
    const hi = parseIp(hiText);
    if (more.length || !lo || !hi || lo.v !== hi.v) return `"${entry}" is not a valid IP range`;
    if (hi.n < lo.n) return `"${entry}" ends before it starts`;
    return { v: lo.v, lo: lo.n, hi: hi.n };
  }
  const [base, bitsStr, ...rest] = e.split('/');
  const addr = parseIp(base ?? '');
  if (!addr || rest.length) return `"${entry}" is not an IP address or range`;
  const width = addr.v === 4 ? 32 : 128;
  if (bitsStr === undefined) return { v: addr.v, lo: addr.n, hi: addr.n };
  const bits = Number(bitsStr);
  if (!/^\d{1,3}$/.test(bitsStr) || bits > width) return `"${entry}" has an invalid range size`;
  const span = 1n << BigInt(width - bits);
  const lo = (addr.n / span) * span;
  return { v: addr.v, lo, hi: lo + span - 1n };
}

/** Why an entry can't be used, or null if it's fine. */
export function allowlistEntryError(entry: string): string | null {
  const r = toRange(entry);
  if (typeof r === 'string') return r;
  if (r.hi - r.lo + 1n > MAX_SPAN[r.v])
    return `"${entry}" is too wide; use /16 or narrower (IPv6: /32)`;
  return null;
}

/** Split pasted text into entries: commas, semicolons, spaces or new lines all separate. */
export function splitAllowlist(text: string): string[] {
  return text
    .replace(/\s*-\s*/g, '-')
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export function ipInAllowlist(ip: string, entries: readonly string[]): boolean {
  const addr = parseIp(ip);
  if (!addr) return false;
  return entries.some((e) => {
    const r = toRange(e);
    return typeof r !== 'string' && r.v === addr.v && addr.n >= r.lo && addr.n <= r.hi;
  });
}
